import {
    OrganizationMarketplace,
    OrganizationMarketplaceServicesInfo,
    OrganizationMarketplaceStaff
} from '@spike/marketplace-model';
import { Element } from 'components/Service/ChipSelector';
import Service, { StaffMember } from 'model/Service';

export interface MarketplaceStaffGroup {
    marketplaceId: number;
    marketplaceName: string;
    staff: Array<StaffMember>;
    totalAvailable: number;
}

const toStaffMember = (staff: OrganizationMarketplaceStaff): StaffMember => ({
    id: staff.id,
    firstName: staff.firstName,
    lastName: staff.lastName,
    avatar: staff.avatar ?? ''
});

export const staffToElement = (staff: OrganizationMarketplaceStaff): Element<number> => ({
    id: staff.id,
    name: `${staff.firstName} ${staff.lastName}`,
    img: staff.avatar || undefined
});

export const buildStaffElements = (staff: Array<OrganizationMarketplaceStaff>): Array<Element<number>> =>
    staff.filter(member => member.active).map(staffToElement);

export const buildSelectedStaffElements = (
    staff: Array<OrganizationMarketplaceStaff>,
    selectedStaffIds: Array<number>
): Array<Element<number>> =>
    staff.filter(member => member.active && selectedStaffIds.includes(member.id)).map(staffToElement);

export const buildServiceStaffFromMarketplaces = (
    marketplaces: Service['marketplaces'],
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>
): Array<StaffMember> => {
    const staffMembers: Array<StaffMember> = [];
    const seenStaffIds = new Set<number>();

    marketplaces.forEach(marketplace => {
        const servicesInfo = organizationServicesInfo.find(info => info.marketplace.id === marketplace.marketplaceId);

        if (!servicesInfo) {
            return;
        }

        marketplace.staffIds.forEach(staffId => {
            if (seenStaffIds.has(staffId)) {
                return;
            }

            const staff = servicesInfo.staff.find(member => member.id === staffId);

            if (staff) {
                seenStaffIds.add(staffId);
                staffMembers.push(toStaffMember(staff));
            }
        });
    });

    return staffMembers.sort((left, right) => left.firstName.localeCompare(right.firstName));
};

export const buildMarketplaceStaffGroups = (
    marketplaces: Service['marketplaces'],
    organizationServicesInfo: Array<OrganizationMarketplaceServicesInfo>,
    organizationMarketplaces: Array<OrganizationMarketplace> = []
): Array<MarketplaceStaffGroup> =>
    marketplaces
        .map(marketplace => {
            const servicesInfo = organizationServicesInfo.find(
                info => info.marketplace.id === marketplace.marketplaceId
            );
            const organizationMarketplace = organizationMarketplaces.find(om => om.id === marketplace.marketplaceId);

            const marketplaceName = servicesInfo?.marketplace.name ?? organizationMarketplace?.name ?? '';

            const staff = marketplace.staffIds
                .map(staffId => servicesInfo?.staff.find(member => member.id === staffId && member.active))
                .filter((member): member is OrganizationMarketplaceStaff => member !== undefined)
                .map(toStaffMember)
                .sort((left, right) => left.firstName.localeCompare(right.firstName));

            return {
                marketplaceId: marketplace.marketplaceId,
                marketplaceName,
                staff,
                totalAvailable: servicesInfo?.staff.filter(member => member.active).length ?? 0
            };
        })
        .filter(group => group.marketplaceName.length > 0)
        .sort((left, right) =>
            left.marketplaceName.toLocaleLowerCase().localeCompare(right.marketplaceName.toLocaleLowerCase())
        );
